import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import SubtleBadge from '@/components/common/SubtleBadge';
import { useAppContext } from '@/Main';
import Icon from './Icon';
import SeoMetricsPanel from './SeoMetricsPanel';
import { difficultyBadge, formatVolume } from '../utils/seoMetrics';
import type { ReactNode } from 'react';
import type { SelectedClusterNode } from '../types';

const darkSurfaceStyle = {
  backgroundColor:
    'color-mix(in srgb, #000 22%, var(--falcon-card-bg))'
};

type Child = SelectedClusterNode['children'][number];
type PinnedNode = Pick<SelectedClusterNode, 'id' | 'label'>;

type SectionLabelProps = {
  children: ReactNode;
};

type ChildRowProps = {
  child: Child;
  onFocus?: (id: string) => void;
};

type ClusterDetailsProps = {
  selected: SelectedClusterNode | null;
  onFocusChild?: (id: string) => void;
  onNavigatePath?: (id: string) => void;
  onTogglePin?: (id: string) => void;
  isPinned?: boolean;
  pinnedNodes?: PinnedNode[];
  onFocusPinned?: (id: string) => void;
  onUnpin?: (id: string) => void;
  locked?: boolean;
  canEditNodes?: boolean;
  editBusy?: boolean;
  onAddChild?: (id: string) => void;
  onEditNode?: (id: string) => void;
  onDeleteNode?: (id: string) => void;
};

function SectionLabel({ children }: SectionLabelProps) {
  return (
    <div className="fs--2 text-600 fw-semi-bold mb-2 text-start">{children}</div>
  );
}

function ChildRow({ child, onFocus }: ChildRowProps) {
  return (
    <button
      type="button"
      className="w-100 d-flex align-items-center justify-content-between gap-2 border-0 border-bottom bg-transparent text-body px-2 py-2 text-start"
      onClick={() => onFocus?.(child.id)}
    >
      <span className="d-flex flex-column gap-1 min-w-0 flex-grow-1 text-start">
        <span className="fw-semi-bold fs--1 text-truncate w-100">{child.label}</span>
        <span className="d-flex flex-wrap gap-1 justify-content-start">
          <Badge bg="primary" pill className="fs--2 text-white">
            {formatVolume(child.seo?.search_volume_miztols)}
          </Badge>
          {child.seo?.difficulty != null ? (
            <SubtleBadge bg={difficultyBadge(child.seo.difficulty)} pill className="fs--2">
              KD {child.seo.difficulty}
            </SubtleBadge>
          ) : null}
          {child.seo?.len != null ? (
            <SubtleBadge bg="secondary" pill className="fs--2">
              L{child.seo.len}
            </SubtleBadge>
          ) : null}
        </span>
      </span>
      {!child.isLeaf ? (
        <SubtleBadge bg="info" pill className="fs--2 flex-shrink-0">
          {child.childCount}
        </SubtleBadge>
      ) : (
        <Icon
          name="chevron-down"
          className="text-600 flex-shrink-0"
          style={{ transform: 'rotate(90deg)' }}
        />
      )}
    </button>
  );
}

export default function ClusterDetails({
  selected,
  onFocusChild,
  onNavigatePath,
  onTogglePin,
  isPinned = false,
  pinnedNodes = [],
  onFocusPinned,
  onUnpin,
  locked = false,
  canEditNodes = false,
  editBusy = false,
  onAddChild,
  onEditNode,
  onDeleteNode
}: ClusterDetailsProps) {
  const {
    config: { isDark }
  } = useAppContext();

  const pathItems =
    (selected?.pathNodes?.length ?? 0) > 0
      ? selected!.pathNodes
      : (selected?.path || []).map((label, i) => ({
          id: `path-${i}`,
          label,
          clickable: false
        }));

  return (
    <div className="h-100 d-flex flex-column">
      <div className="flex-grow-1 d-flex flex-column overflow-hidden min-h-0 bg-transparent">
        {!selected ? (
          <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-center px-3 py-4">
            <div
              className="mb-3 rounded-circle d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10"
              style={{
                width: '3rem',
                height: '3rem',
                color: 'var(--falcon-navbar-vertical-default-link-active-color)'
              }}
            >
              <Icon name="diagram-3" size="1.75rem" />
            </div>
            <div className="fw-semi-bold mb-2">جزئیات نود</div>
            <p className="text-600 mb-0 small mx-auto" style={{ maxWidth: 220 }}>
              روی یک کلمه یا خوشه کلیک کنید تا میزان جستجو، سختی و نیت را ببینید.
              برای ویرایش درخت، راست‌کلیک کنید یا از دکمه‌های نوار جزئیات استفاده
              کنید.
            </p>
          </div>
        ) : (
          <>
            <div className="px-3 pt-3 pb-2 border-bottom">
              <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="fs--2 text-600 mb-2">
                    {selected.isLeaf ? 'کلمه کلیدی' : 'خوشه معنایی'}
                  </div>
                  <h6 className="fw-semi-bold mb-0 text-truncate fs-0" title={selected.label}>
                    {selected.label}
                  </h6>
                </div>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      {isPinned ? 'برداشتن پین' : 'پین کردن'}
                    </Tooltip>
                  }
                >
                  <Button
                    size="sm"
                    variant={isPinned ? 'warning' : 'falcon-default'}
                    className={`flex-shrink-0 d-inline-flex align-items-center justify-content-center lh-1 p-0${
                      isPinned ? '' : ' text-600'
                    }`}
                    style={{ width: '1.35rem', height: '1.35rem' }}
                    disabled={locked}
                    onClick={() => onTogglePin?.(selected.id)}
                    aria-label={isPinned ? 'برداشتن پین' : 'پین کردن'}
                  >
                    <Icon
                      name={isPinned ? 'pin-angle-fill' : 'pin-angle'}
                      size="0.55rem"
                    />
                  </Button>
                </OverlayTrigger>
              </div>
              <div className="d-flex align-items-center gap-2 mt-2">
                <div className="d-flex flex-wrap align-items-center gap-1 min-w-0">
                  <Badge bg="primary" pill className="fs--2 text-white">
                    عمق {selected.depth}
                  </Badge>
                  {!selected.isLeaf && (
                    <SubtleBadge bg="info" pill className="fs--2">
                      {selected.childCount} زیرشاخه
                    </SubtleBadge>
                  )}
                </div>
                {canEditNodes ? (
                  <div className="d-flex align-items-center gap-2 ms-auto flex-shrink-0">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>افزودن</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="falcon-primary"
                        className="d-inline-flex align-items-center justify-content-center lh-1 p-0"
                        style={{ width: '1.35rem', height: '1.35rem' }}
                        disabled={locked || editBusy}
                        onClick={() => onAddChild?.(selected.id)}
                        aria-label="افزودن"
                      >
                        <Icon name="plus-lg" size="0.55rem" />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>ویرایش</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="falcon-default"
                        className="d-inline-flex align-items-center justify-content-center lh-1 p-0 text-600"
                        style={{ width: '1.35rem', height: '1.35rem' }}
                        disabled={locked || editBusy}
                        onClick={() => onEditNode?.(selected.id)}
                        aria-label="ویرایش"
                      >
                        <Icon name="pencil" size="0.55rem" />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>حذف</Tooltip>}
                    >
                      <span className="d-inline-flex">
                        <Button
                          size="sm"
                          variant="falcon-danger"
                          className="d-inline-flex align-items-center justify-content-center lh-1 p-0"
                          style={{ width: '1.35rem', height: '1.35rem' }}
                          disabled={locked || editBusy || selected.depth === 0}
                          onClick={() => onDeleteNode?.(selected.id)}
                          aria-label="حذف"
                        >
                          <Icon name="trash" size="0.55rem" />
                        </Button>
                      </span>
                    </OverlayTrigger>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex-grow-1 overflow-auto px-3 py-3">
              {pinnedNodes.length > 0 ? (
                <div
                  className={`mb-3 rounded-3 px-2 py-2 border${isDark ? '' : ' bg-white'}`}
                  style={isDark ? darkSurfaceStyle : undefined}
                >
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      className={`rounded-circle d-inline-flex align-items-center justify-content-center text-warning flex-shrink-0${
                        isDark ? '' : ' bg-200'
                      }`}
                      style={
                        isDark
                          ? {
                              width: '1.75rem',
                              height: '1.75rem',
                              backgroundColor:
                                'color-mix(in srgb, var(--falcon-body-color) 8%, transparent)'
                            }
                          : { width: '1.75rem', height: '1.75rem' }
                      }
                    >
                      <Icon name="pin-angle-fill" style={{ fontSize: '0.85rem' }} />
                    </div>
                    <div className="fs--1 text-600 flex-grow-1 min-w-0">
                      پین‌شده‌ها
                    </div>
                    <SubtleBadge bg="warning" pill className="fs--2 flex-shrink-0">
                      {pinnedNodes.length}
                    </SubtleBadge>
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {pinnedNodes.map(node => (
                      <div
                        key={node.id}
                        className="d-inline-flex align-items-center gap-1 rounded-pill border px-2 py-1"
                        style={{ maxWidth: '100%' }}
                      >
                        <button
                          type="button"
                          className="border-0 bg-transparent p-0 text-body fs--2 text-truncate fw-medium"
                          style={{ maxWidth: '9rem', cursor: 'pointer' }}
                          title={node.label}
                          onClick={() => onFocusPinned?.(node.id)}
                        >
                          {node.label}
                        </button>
                        <button
                          type="button"
                          className="border-0 bg-transparent p-0 text-500 d-inline-flex align-items-center justify-content-center lh-1"
                          style={{
                            cursor: 'pointer',
                            width: '0.85rem',
                            height: '0.85rem',
                            opacity: 0.65
                          }}
                          onClick={() => onUnpin?.(node.id)}
                          aria-label="حذف پین"
                        >
                          <Icon name="x-lg" size="0.5rem" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <SeoMetricsPanel seo={selected.seo} />

              <div className="mt-3">
                <SectionLabel>مسیر از ریشه</SectionLabel>
                <div className="d-flex flex-wrap align-items-center gap-1">
                  {pathItems.map((item, i) => {
                    const isLast = i === pathItems.length - 1;
                    const canNavigate =
                      !isLast && item.id && !String(item.id).startsWith('path-');
                    return (
                      <span
                        key={`${item.id}-${i}`}
                        className="d-inline-flex align-items-center"
                      >
                        {i > 0 ? (
                          <span
                            className="text-500 mx-1 d-inline-flex align-items-center"
                            style={{ transform: 'rotate(90deg)' }}
                          >
                            <Icon name="chevron-down" size="0.65rem" />
                          </span>
                        ) : null}
                        {canNavigate ? (
                          <button
                            type="button"
                            className="border-0 bg-transparent p-0"
                            style={{ cursor: 'pointer' }}
                            onClick={() => onNavigatePath?.(item.id)}
                            title={`رفتن به ${item.label}`}
                          >
                            <SubtleBadge
                              bg="secondary"
                              pill
                              className="fs--2 fw-medium text-wrap text-end"
                            >
                              {item.label}
                            </SubtleBadge>
                          </button>
                        ) : (
                          <SubtleBadge
                            bg={isLast ? 'success' : 'secondary'}
                            pill
                            className="fs--2 fw-medium text-wrap text-end"
                          >
                            {item.label}
                          </SubtleBadge>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>

              {!selected.isLeaf && selected.children?.length > 0 ? (
                <div className="mt-3">
                  <SectionLabel>
                    زیرشاخه‌ها ({selected.children.length})
                  </SectionLabel>
                  <div
                    className={`rounded-2 border pb-2${isDark ? '' : ' bg-white'}`}
                    style={isDark ? darkSurfaceStyle : undefined}
                  >
                    {selected.children.map(child => (
                      <ChildRow
                        key={child.id}
                        child={child}
                        onFocus={onFocusChild}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
